import {merge, combineLatest} from 'rxjs';
import {filter, map, scan} from 'rxjs/operators';
import {wsLogout$} from '@/user/auth-ws';
import {aopMsg$, aopMsgV2$} from '@/raw/aop';
import {contractAccountCurrencies$, riskEngineMode$} from '@/config';
import {rasAccountCache$} from '@/ras/ras';
import type {ws, ReduceAction} from '@/types';

type ContractAccountRaw = ws.ContractAccountRaw;
type Cache = Map<string, ContractAccountRaw>;
type Action = ReduceAction<Cache>;

const accounts$ = aopMsg$.pipe(
  filter((o) => !!o.accounts),
  map((o) => o.accounts)
);

const aopMsgV2Raw$ = aopMsgV2$.pipe(
  filter((o) => !!o.accounts),
  map((o) => o.accounts)
);

const accounts2$ = combineLatest([aopMsgV2Raw$, riskEngineMode$, rasAccountCache$]).pipe(map(convertAccountV2));

export function convertAccountV2([accounts, riskEngineMode, rasAccountCache]) {
  return accounts.map(
    ({unrealisedPnlRv, userID, currency, accountID, accountBalanceRv, totalUsedBalanceRv, bonusBalanceRv}) => {
      let accountBalanceEv: string;
      let totalUsedBalanceEv: string;
      let bonusBalanceEv: string;
      let isolatedTotalBalanceEv: string;

      // 目前仅支持：USDT、MUSDT资产从ras中读取
      if (['USDT', 'MUSDT'].includes(currency) && rasAccountCache.get(currency)) {
        const raw = {
          totalBalanceRv: accountBalanceRv,
          totalUsedBalanceRv,
          totalBonusRv: bonusBalanceRv,
          isolatedTotalBalanceRv: 0,
        };
        const account = riskEngineMode ? rasAccountCache.get(currency) : raw;
        accountBalanceEv = account.totalBalanceRv;
        totalUsedBalanceEv = account.totalUsedBalanceRv;
        bonusBalanceEv = account.totalBonusRv;
        isolatedTotalBalanceEv = account.isolatedTotalBalanceRv;
      } else {
        accountBalanceEv = accountBalanceRv;
        totalUsedBalanceEv = totalUsedBalanceRv;
        bonusBalanceEv = bonusBalanceRv;
        isolatedTotalBalanceEv = '0';
      }
      return {
        accountBalanceEv,
        accountID,
        bonusBalanceEv,
        currency,
        totalUsedBalanceEv,
        unrealisedPnlEv: unrealisedPnlRv,
        userID,
        isolatedTotalBalanceEv,
      };
    }
  );
}

const accountInit$ = contractAccountCurrencies$.pipe(
  map((accountCurrencies): Action => {
    return function (cache: Cache) {
      return Array.from(new Set(accountCurrencies)).reduce((p, currency) => {
        if (p.has(currency)) {
          return p;
        }
        return p.set(currency, {
          accountBalanceEv: 0,
          accountID: 0,
          bonusBalanceEv: 0,
          currency,
          totalUsedBalanceEv: 0,
          userID: 0,
          tradeLevel: 0,
          isolatedTotalBalanceEv: 0,
        });
      }, cache);
    };
  })
);

const accountUpdate$ = accounts$.pipe(
  map((accounts: ContractAccountRaw[]): Action => {
    return function (cache: Cache) {
      accounts.forEach((account: ContractAccountRaw) => cache.set(account.currency, account));
      return cache;
    };
  })
);

const accountUpdate2$ = accounts2$.pipe(
  map((accounts: ContractAccountRaw[]): Action => {
    return function (cache: Cache) {
      accounts.forEach((account: ContractAccountRaw) => cache.set(account.currency, account));
      return cache;
    };
  })
);

const accountReset$ = wsLogout$.pipe(
  map((): Action => {
    return function (cache: Cache) {
      cache.clear();
      return cache;
    };
  })
);

export const contractAccountsCache$ = merge<Action[]>(
  accountInit$,
  accountUpdate$,
  accountUpdate2$,
  accountReset$
).pipe(scan((p: Cache, action: Action) => action(p), new Map<string, ContractAccountRaw>()));
